<template>
  <section
    v-if="!loading && testimonials.length > 0"
    id="testimonials"
    class="py-14 sm:py-18 bg-gradient-to-b from-blue-50/70 to-indigo-50/40 dark:from-slate-900/50 dark:to-slate-950/70 rounded-3xl my-8 overflow-hidden relative border border-blue-100/70 dark:border-slate-800/70"
  >
    <!-- Background Glow Accent -->
    <div class="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-amber-400/10 dark:bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

    <div class="container mx-auto px-4 sm:px-6 relative z-10">
      
      <!-- Section Header -->
      <div class="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
        <div class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-black uppercase tracking-wider mb-2.5 shadow-sm">
          <span>⭐ {{ stats.average_rating || '4.9' }}/5 • {{ stats.total_reviews ? `${stats.total_reviews} Verified Reviews` : '100% Verified Customer Reviews' }}</span>
        </div>
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
          What Our Customers Say
        </h2>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1.5">
          Real feedback from verified buyers across digital codes, gaming top-ups, hardware, and overall platform experience.
        </p>
      </div>

    </div>

    <!-- Continuous Auto Marquee Ticker Track -->
    <div
      class="relative overflow-hidden w-full cursor-pointer select-none group py-2"
      @mouseenter="isPaused = true"
      @mouseleave="isPaused = false"
      @touchstart="isPaused = true"
      @touchend="isPaused = false"
    >
      <!-- Left & Right Gradient Fade Edges -->
      <div class="pointer-events-none absolute left-0 top-0 bottom-0 w-8 sm:w-20 bg-gradient-to-r from-blue-50/90 dark:from-slate-900/90 to-transparent z-10"></div>
      <div class="pointer-events-none absolute right-0 top-0 bottom-0 w-8 sm:w-20 bg-gradient-to-l from-indigo-50/90 dark:from-slate-950/90 to-transparent z-10"></div>

      <!-- Marquee Sliding Track -->
      <div
        class="testimonials-marquee-track flex items-stretch gap-4 sm:gap-6 will-change-transform px-4"
        :style="{ animationPlayState: isPaused ? 'paused' : 'running' }"
      >
        <!-- Seamless Infinite Loop List -->
        <div
          v-for="(item, index) in displayList"
          :key="`${item.id}-${index}`"
          class="w-[280px] sm:w-[340px] md:w-[380px] shrink-0"
        >
          <TestimonialCard :testimonial="item" />
        </div>
      </div>
    </div>

  </section>

  <!-- Loading Skeleton State -->
  <section
    v-else-if="loading"
    class="py-14 bg-blue-50/40 dark:bg-slate-900/40 rounded-3xl my-8 px-4 sm:px-6"
  >
    <div class="container mx-auto">
      <div class="h-6 w-48 bg-slate-200 dark:bg-slate-800 rounded-xl mb-3 animate-pulse mx-auto"></div>
      <div class="h-9 w-72 bg-slate-200 dark:bg-slate-800 rounded-xl mb-8 animate-pulse mx-auto"></div>
      <div class="flex gap-4 overflow-hidden">
        <div v-for="i in 3" :key="i" class="w-[340px] shrink-0 h-52 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-pulse"></div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import TestimonialCard from './TestimonialCard.vue';
import { ratingService } from '@/services/api';

export default {
  name: 'TestimonialsSection',
  components: {
    TestimonialCard
  },
  emits: ['stats-loaded'],
  setup(props, { emit }) {
    const testimonials = ref([]);
    const stats = ref({
      average_rating: '4.9',
      total_reviews: 0,
      five_star_count: 0,
      global_count: 0
    });
    const loading = ref(true);
    const isPaused = ref(false);

    // Repeated list for seamless infinite looping marquee
    const displayList = computed(() => {
      if (testimonials.value.length === 0) return [];
      if (testimonials.value.length < 4) {
        return [
          ...testimonials.value,
          ...testimonials.value,
          ...testimonials.value,
          ...testimonials.value
        ];
      }
      return [...testimonials.value, ...testimonials.value];
    });

    // Fetch dynamic reviews and stats from API
    const fetchReviews = async () => {
      loading.value = true;
      try {
        const res = await ratingService.getTestimonials();
        const rawReviews = (res && res.testimonials) ? res.testimonials : [];

        if (res && res.stats) {
          stats.value = {
            average_rating: String(res.stats.average_rating || '4.9'),
            total_reviews: Number(res.stats.total_reviews || rawReviews.length),
            five_star_count: Number(res.stats.five_star_count || 0),
            global_count: Number(res.stats.global_count || 0)
          };
          emit('stats-loaded', stats.value);
        }

        // Prioritize: 5-stars & global reviews first, then by comment length & latest
        const sorted = [...rawReviews].sort((a, b) => {
          // If one is 5 stars and other is not
          if (b.rating !== a.rating) {
            return (b.rating || 0) - (a.rating || 0);
          }
          // Prioritize global reviews if equal rating
          if (b.is_global && !a.is_global) return 1;
          if (!b.is_global && a.is_global) return -1;

          const lenA = (a.comment || a.text || '').trim().length;
          const lenB = (b.comment || b.text || '').trim().length;
          if (lenB !== lenA) {
            return lenB - lenA; // Longest text first
          }
          return (b.id || 0) - (a.id || 0); // Newest first
        });

        testimonials.value = sorted.slice(0, 15);
      } catch (err) {
        console.warn('Could not fetch real testimonials:', err);
        testimonials.value = [];
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchReviews();
    });

    return {
      testimonials,
      stats,
      displayList,
      loading,
      isPaused
    };
  }
};
</script>

<style scoped>
.testimonials-marquee-track {
  display: flex;
  width: max-content;
  animation: testimonials-marquee 42s linear infinite;
}

@keyframes testimonials-marquee {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>